/**
 * Global semaphore that limits concurrent favicon/avatar image loads to MAX_CONCURRENT.
 *
 * Without this, rendering a list of N email threads fires N favicon requests
 * simultaneously. All N backend-to-favicon-service round-trips complete at roughly
 * the same time, flooding the JS event queue with response callbacks and blocking the
 * main thread. By throttling to MAX_CONCURRENT loads we spread the callbacks out.
 *
 * 12 concurrent loads is a reasonable balance: fast enough that a typical inbox
 * (30-50 unique senders) is fully rendered within a few seconds on first load,
 * while still keeping the main-thread callback burst manageable.
 */

const MAX_CONCURRENT = 12;

let _active = 0;
const _waiters: Array<() => void> = [];

function _drain() {
  while (_waiters.length > 0 && _active < MAX_CONCURRENT) {
    _active++;
    _waiters.shift()!();
  }
}

/**
 * Acquire a favicon loading slot. Resolves immediately if a slot is free,
 * otherwise queues until one is released. Returns a release function that
 * MUST be called when the image finishes loading or errors (or on unmount).
 */
/** How many slots are currently occupied (for diagnostics). */
export function getFaviconQueueStats() {
  return { active: _active, waiting: _waiters.length, max: MAX_CONCURRENT };
}

export function acquireFaviconSlot(): Promise<() => void> {
  return new Promise((resolve) => {
    const acquire = () => {
      _active++;
      let released = false;
      resolve(() => {
        if (!released) {
          released = true;
          _active = Math.max(0, _active - 1);
          _drain();
        }
      });
    };

    if (_active < MAX_CONCURRENT) {
      acquire();
    } else {
      // Queue is saturated — log once when it first fills so it's visible in devtools.
      if (_waiters.length === 0) {
        console.debug(`[faviconQueue] saturated (${_active}/${MAX_CONCURRENT} active), queuing remaining loads`);
      }
      _waiters.push(acquire);
    }
  });
}
